// ReturnWizard — one Mantine Stepper drives all 5 buyer create-return steps.
//
// 03-menu-structure §4.1.4 — DRY across the three entry paths:
//   Path A  <ReturnWizard orderId preselectedItemIds /> — Step 0 skipped
//   Path B  <ReturnWizard />                            — Step 0 (picker) shown
//   Path C  <ReturnWizard orderId />                    — Step 0 skipped
//
// Thinking-in-React: the wizard owns ALL wizard state (useReducer, NOT Redux);
// each step is a props-only child fed slices of state + dispatch/callbacks.
// Server data (the chosen order's detail) comes from SWR.
//
// ── BACKEND CONTRACT NOTE (create payload) ──
// `preferredRefundMethod` is now accepted by the create Joi (optional, D2) and
// is sent with the payload. `shippingResponsibility` and `subReason` are NOT in
// the create Joi (unknown keys REJECT → 400) and are intentionally omitted —
// shippingResponsibility is the supplier's propose-time decision (D11), and
// subReason is FE-only (C8).
import { Box, Button, Group, Loader, Text } from "@mantine/core";
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, PaperPlaneTiltIcon } from "@phosphor-icons/react";
import { useEffect, useMemo, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { ReturnApi } from "../../../../apis";
import type {
  ICreateReturnBody,
  ICreateReturnItem,
  IReturnOrderDetail,
  ReturnReason,
} from "../../../../apis/return";
import HttpHandler from "../../../../utils/http-handler";
import { message } from "../../../../utils/message";
import { BUYER_RETURNS_ROUTES } from "../constants";
import classes from "./wizard-stepper.module.css";
import { ReturnActiveExistsError, ReturnNetworkError } from "../return-error-state";
import { StepItemSelect } from "./StepItemSelect";
import { StepOrderPicker } from "./StepOrderPicker";
import { StepPhotosRefund } from "./StepPhotosRefund";
import { StepReason } from "./StepReason";
import { StepReview } from "./StepReview";
import {
  detailValid,
  initialWizardState,
  reasonRequiresDetail,
  selectedCount,
  selectedItems,
  wizardReducer,
  type WizardSelectedItem,
} from "./state";

export interface ReturnWizardProps {
  orderId?: string;
  preselectedItemIds?: string[];
  initialReason?: ReturnReason;
}

const DELIVERED_STATUSES = ["DELIVERED", "CLOSED", "COMPLETED"];

const orderNumberOf = (o?: IReturnOrderDetail | null): string =>
  (o?.purchaseCode || o?.salesCode || o?.orderNumber || o?.id) ?? "";

// Seller (counterparty) — buyer's purchase order → receiverBusiness; SALES → sender.
function resolveSeller(order: IReturnOrderDetail): { id: string | null; name: string | null } {
  const type = (order as { type?: string }).type;
  const biz = type === "SALES" ? order.senderBusiness : order.receiverBusiness;
  const id =
    (biz?.id as string | undefined) ??
    (type === "SALES"
      ? ((order as { senderBusinessId?: string }).senderBusinessId)
      : ((order as { receiverBusinessId?: string }).receiverBusinessId)) ??
    null;
  const name = (biz?.profileName as string | undefined) ?? (biz?.partner?.businessName as string | undefined) ?? null;
  return { id, name };
}

export function ReturnWizard({ orderId, preselectedItemIds, initialReason }: ReturnWizardProps) {
  const { t } = useTranslation("buyerApp");
  const navigate = useNavigate();
  const hasOrderProp = !!orderId;

  const [state, dispatch] = useReducer(wizardReducer, initialWizardState(orderId ?? null));
  // Stepper index. With an order prop (Path A/C) Step 0 is hidden, so the first
  // visible step (Item select) is index 0 in BOTH numbering schemes below.
  const [active, setActive] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  // RET-005 recovery: the conflicting line's existing RET- number + product name,
  // derived client-side from the Phase-1 order aggregate (line.returnNumbers).
  const [activeExists, setActiveExists] = useState<{ productName?: string; returnNumber?: string } | null>(null);
  const [showItemError, setShowItemError] = useState(false);
  const [showPhotoError, setShowPhotoError] = useState(false);
  const [itemsLoadedFor, setItemsLoadedFor] = useState<string | null>(null);

  // The chosen order: prop (Path A/C) or Step-0 selection (Path B).
  const effectiveOrderId = state.orderId;

  const { data: order, error: orderError, isLoading: orderLoading } = useSWR(
    effectiveOrderId ? ["swr.buyer.returns.order-for-return", effectiveOrderId] : null,
    () => ReturnApi.getOrderForReturn(effectiveOrderId as string),
    { revalidateOnFocus: false },
  );

  // When the order detail arrives, hydrate the wizard item map ONCE per order.
  useEffect(() => {
    if (!order || itemsLoadedFor === order.id) return;
    const items: WizardSelectedItem[] = (order.lines ?? []).map((line) => ({
      orderItemId: line.id,
      selected: false,
      quantity: 1,
      available: line.availableReturnQuantity ?? 0,
      productName: line.name || "-",
      unitPrice: Number(line.price) || 0,
      sku: line.skuCode ?? null,
      image: line.image ?? null,
      unit: line.unit ?? null,
      orderedQuantity: Number(line.quantity) || 0,
      alreadyReturned: line.alreadyReturnedQuantity ?? 0,
      note: "",
    }));
    dispatch({ type: "LOAD_ITEMS", items, preselectedItemIds });
    // initial reason hint (Path A/C convenience)
    if (initialReason) dispatch({ type: "SET_REASON", reason: initialReason });
    setItemsLoadedFor(order.id);
    // preselectedItemIds / initialReason are entry-time props; intentionally
    // not in deps so a later prop identity change can't re-hydrate mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, itemsLoadedFor]);

  const isDelivered = useMemo(() => {
    if (!order) return true;
    const status = (order.orderStatus ?? "").toUpperCase();
    return order.deliveredAt != null || DELIVERED_STATUSES.includes(status);
  }, [order]);

  const requestType: "RETURN" | "SIMPLE" = isDelivered ? "RETURN" : "SIMPLE";
  const seller = useMemo(() => (order ? resolveSeller(order) : { id: null, name: null }), [order]);

  // B67: refund ETA per method from the BE (seller's tenant refund SLA) instead of
  // static copy. A failure just leaves "-" in the ETA lines.
  const { data: etaData } = useSWR(
    order && seller.id ? ["swr.buyer.returns.refund-eta", requestType, seller.id] : null,
    () => ReturnApi.previewRefundTimeline({ requestType, sellerBusinessId: seller.id as string }),
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  const refundEta = useMemo(
    () => Object.fromEntries((etaData?.previews ?? []).map((p) => [String(p.refundMethod), p])),
    [etaData],
  );

  // Steps array — Step 0 only when no order prop AND no order chosen yet matters
  // for the picker visibility, but the Stepper always shows 5 conceptual steps.
  // For Path A/C we drop the picker step entirely (5 → but render 5 labels with
  // Step 0 marked complete is noisier; instead we render the picker step only
  // when needed and keep a single offset).
  const showPickerStep = !hasOrderProp;

  // ── Per-step validation gates ──
  const itemsValid = selectedCount(state) >= 1;
  const reasonValid = !!state.reason && (!reasonRequiresDetail(state.reason) || detailValid(state));
  const photosValid = requestType === "SIMPLE" || state.evidencePhotos.length >= 1;
  const reviewValid = state.consent && itemsValid && reasonValid && photosValid && !!seller.id;

  // Map the visible step index → which content to render.
  // With picker: [0]=picker, [1]=items, [2]=reason, [3]=photos, [4]=review
  // Without:     [0]=items,  [1]=reason, [2]=photos, [3]=review
  const STEP = showPickerStep
    ? { picker: 0, items: 1, reason: 2, photos: 3, review: 4 }
    : { picker: -1, items: 0, reason: 1, photos: 2, review: 3 };
  const lastStep = STEP.review;

  const goNext = () => {
    if (active === STEP.items && !itemsValid) {
      setShowItemError(true);
      return;
    }
    if (active === STEP.photos && !photosValid) {
      setShowPhotoError(true);
      return;
    }
    setActive((s) => Math.min(s + 1, lastStep));
  };
  const goBack = () => setActive((s) => Math.max(s - 1, 0));

  const onPickOrder = (pickedId: string) => {
    dispatch({ type: "SET_ORDER", orderId: pickedId });
    setItemsLoadedFor(null);
    setActive(STEP.items);
  };

  const buildPayload = (): ICreateReturnBody | null => {
    if (!order || !seller.id) return null;
    const chosen = selectedItems(state);
    if (chosen.length === 0) return null;

    const items: ICreateReturnItem[] = chosen.map((it) => ({
      orderItemId: it.orderItemId,
      quantity: it.quantity,
      returnReason: state.reason as ReturnReason,
      unitPrice: it.unitPrice,
      productName: it.productName,
      description: it.note.trim() ? it.note.trim() : null,
      sku: it.sku ?? null,
    }));

    // FULL when every returnable line is selected at its full ordered quantity.
    const allLines = state.itemOrder.map((id) => state.items[id]);
    const isFull =
      allLines.length > 0 &&
      allLines.every((l) => l.selected && l.quantity >= l.orderedQuantity);

    return {
      requestType,
      orderId: order.id,
      returnType: isFull ? "FULL" : "PARTIAL",
      returnReason: state.reason as ReturnReason,
      reasonDetails: state.reasonDetails.trim() ? state.reasonDetails.trim() : null,
      items,
      evidencePhotos: state.evidencePhotos,
      sellerBusinessId: seller.id,
      sellerBusinessName: seller.name,
      orderNumber: orderNumberOf(order),
      invoiceId: order.invoiceId ?? null,
      invoiceNumber: order.invoiceNumber ?? null,
      preferredRefundMethod: state.preferredRefundMethod ?? null,
      // shippingResponsibility and subReason are NOT in the create Joi — omitted intentionally.
    };
  };

  // RET-005 recovery target — the existing active return on a selected line.
  // The create error carries no existing id/number, so we derive it from the
  // Phase-1 order aggregate (line.returnNumbers) the wizard already loaded.
  const existingReturnRef = (): { productName?: string; returnNumber?: string } => {
    const chosenIds = new Set(selectedItems(state).map((it) => it.orderItemId));
    const line = (order?.lines ?? []).find(
      (l) => chosenIds.has(l.id) && (l.returnNumbers?.length ?? 0) > 0,
    );
    return { productName: line?.name ?? undefined, returnNumber: line?.returnNumbers?.[0] };
  };

  const onSubmit = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setSubmitting(true);
    setActiveExists(null);
    try {
      const res = await ReturnApi.createReturn(payload);
      const number = res?.returnNumber ?? "RET";
      const completed = res?.status === "COMPLETED";
      message.success(
        completed
          ? t("wizard.createdImmediate", { number })
          : t("wizard.created", { number }),
      );
      if (res?.returnRequestId) {
        navigate(BUYER_RETURNS_ROUTES.detail(res.returnRequestId));
      } else {
        navigate(BUYER_RETURNS_ROUTES.hub);
      }
    } catch (err) {
      // ReturnApi does NOT auto-toast. RET-005 (an active return already exists
      // for one of the items, §24) shows the inline recovery card; every OTHER
      // error (RET-046 no-warehouse, RET-009 evidence-required, validation 400s,
      // network) must surface an explicit toast here.
      const h = err as HttpHandler;
      const msg = h?.message ?? "";
      if (typeof msg === "string" && /active_return_exists|RET-005|RET_005/i.test(msg)) {
        setActiveExists(existingReturnRef());
      } else {
        message.error(h?.message || t("wizard.createError"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ──

  // Path B before an order is chosen: only the picker step is meaningful.
  const pickerContent = (
    <StepOrderPicker onPick={onPickOrder} onViewOrders={() => navigate("/buyer-sales")} />
  );

  if (orderError) {
    return <ReturnNetworkError onRetry={() => window.location.reload()} />;
  }

  const summaryOrder = {
    orderNumber: orderNumberOf(order),
    deliveredAt: order?.deliveredAt ?? null,
  };

  // Step labels as drawn: Path B shows «Захиалга» first; Path A/C start at «Бараа».
  const stepLabels = [
    ...(showPickerStep ? [t("wizard.stepOrder")] : []),
    t("wizard.stepItem"),
    t("wizard.stepReason"),
    t("wizard.stepPhoto"),
    t("wizard.stepSubmit"),
  ];

  return (
    <Box>
      {/* Stepper card (canvas OrdB_ReturnNew): steps only; the body renders in its own card below. */}
      <Box className={`${classes.card} ${classes.stepsCard}`}>
        <Box component="ol" className={classes.steps}>
          {stepLabels.map((label, i) => (
            <Box
              component="li"
              key={label}
              className={classes.step}
              data-reached={i <= active || undefined}
              data-current={i === active || undefined}
              aria-current={i === active ? "step" : undefined}
            >
              {i > 0 && <Box component="span" className={classes.line} aria-hidden />}
              <Box component="span" className={classes.dot}>
                {i < active ? <CheckIcon size={16} weight="bold" /> : i + 1}
              </Box>
              <Text span className={classes.label}>
                {label}
              </Text>
            </Box>
          ))}
        </Box>
      </Box>

      <Box className={`${classes.card} ${classes.bodyCard}`}>
        {active === STEP.picker ? pickerContent : null}

        {active === STEP.items ? (
          <>
            {orderLoading || !order ? (
              <Group justify="center" py="xl">
                <Loader size="sm" />
              </Group>
            ) : (
              <StepItemSelect order={summaryOrder} state={state} dispatch={dispatch} />
            )}
            {showItemError && !itemsValid ? (
              <Box mt="sm" c="red" fz="sm">
                {t("wizard.itemErrorAtLeastOne")}
              </Box>
            ) : null}
          </>
        ) : null}

        {active === STEP.reason ? (
          <StepReason
            isDelivered={isDelivered}
            reason={state.reason}
            subReason={state.subReason}
            reasonDetails={state.reasonDetails}
            onReason={(reason) => dispatch({ type: "SET_REASON", reason })}
            onSubReason={(value) => dispatch({ type: "SET_SUB_REASON", subReason: value })}
            onReasonDetails={(value) => dispatch({ type: "SET_REASON_DETAILS", reasonDetails: value })}
          />
        ) : null}

        {active === STEP.photos ? (
          <StepPhotosRefund
            requestType={requestType}
            photos={state.evidencePhotos}
            refundMethod={state.preferredRefundMethod}
            shipping={state.shippingResponsibility}
            photoError={showPhotoError && !photosValid ? t("wizard.photoErrorRequired") : null}
            refundEta={refundEta}
            onPhotos={(urls) => dispatch({ type: "SET_PHOTOS", photos: urls })}
            onRefundMethod={(method) => dispatch({ type: "SET_REFUND_METHOD", method })}
            onShipping={(shipping) => dispatch({ type: "SET_SHIPPING", shipping })}
          />
        ) : null}

        {active === STEP.review ? (
          <>
            {activeExists ? (
              <Box mb="md">
                <ReturnActiveExistsError
                  productName={activeExists.productName}
                  returnNumber={activeExists.returnNumber}
                  onGoToReturn={() =>
                    navigate(
                      activeExists.returnNumber
                        ? `${BUYER_RETURNS_ROUTES.hub}?search=${encodeURIComponent(activeExists.returnNumber)}`
                        : BUYER_RETURNS_ROUTES.hub,
                    )
                  }
                />
              </Box>
            ) : null}
            <StepReview
              order={summaryOrder}
              state={state}
              reason={state.reason}
              sellerName={seller.name}
              refundEta={refundEta}
              onConsent={(value) => dispatch({ type: "SET_CONSENT", consent: value })}
            />
          </>
        ) : null}
      </Box>

      {/* ── Footer nav ── */}
      {!(showPickerStep && active === STEP.picker) ? (
        <Group justify="space-between" mt="md">
          <Button
            variant="default"
            leftSection={<ArrowLeftIcon size={18} />}
            onClick={active === STEP.items && showPickerStep ? () => setActive(STEP.picker) : goBack}
            disabled={active === STEP.items && !showPickerStep}
          >
            {t("wizard.back")}
          </Button>

          {active < lastStep ? (
            <Button
              rightSection={<ArrowRightIcon size={18} />}
              onClick={goNext}
              disabled={
                (active === STEP.reason && !reasonValid) ||
                (active === STEP.items && !order)
              }
            >
              {t("wizard.continue")}
            </Button>
          ) : (
            <Button
              color="brand"
              leftSection={<PaperPlaneTiltIcon size={18} />}
              loading={submitting}
              disabled={!reviewValid}
              onClick={onSubmit}
            >
              {t("wizard.submit")}
            </Button>
          )}
        </Group>
      ) : null}
    </Box>
  );
}
